if (!customElements.get('collection-grid-toggle')) {
  class CollectionGridToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.storageKey = this.dataset.storageKey || 'collection-grid-cols';
      this.defaultCols = this.getAttribute('data-cols') || '4';
      this.collection = this.closest('.collection');

      this.allowedCols = ['3', '4', '5'];
      this.cols = this.defaultCols;

      /*
       * Keep an exact copy of the theme's original button contents.
       * This is used for the original 3-column icon.
       */
      if (this.button) {
        this.originalButtonHTML = this.button.innerHTML;
      }

      if (!(window.Shopify && window.Shopify.designMode)) {
        try {
          const saved = JSON.parse(
            localStorage.getItem(this.storageKey) || 'null'
          );

          if (
            saved &&
            saved.base === this.defaultCols &&
            this.allowedCols.includes(saved.cols)
          ) {
            this.cols = saved.cols;
          }
        } catch (e) {
          /* storage unavailable */
        }
      }

      this.addGridIconStyles();
      this.apply();

      if (this.button) {
        this.button.addEventListener('click', () => {
          const currentIndex = this.allowedCols.indexOf(this.cols);
          const nextIndex = (currentIndex + 1) % this.allowedCols.length;

          this.cols = this.allowedCols[nextIndex];

          try {
            localStorage.setItem(
              this.storageKey,
              JSON.stringify({
                cols: this.cols,
                base: this.defaultCols
              })
            );
          } catch (e) {
            /* storage unavailable */
          }

          this.apply();
        });
      }
    }

    addGridIconStyles() {
      if (document.getElementById('collection-grid-toggle-custom-icon-styles')) {
        return;
      }

      const style = document.createElement('style');

      style.id = 'collection-grid-toggle-custom-icon-styles';

      style.textContent = `
        collection-grid-toggle .collection-grid-toggle__custom-icon {
          width: 100%;
          height: 100%;
        }

        collection-grid-toggle .collection-grid-toggle__custom-icon--4 {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2px;
        }

        collection-grid-toggle .collection-grid-toggle__custom-icon--5 {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
        }

        collection-grid-toggle .collection-grid-toggle__custom-icon-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2px;
        }

        collection-grid-toggle .collection-grid-toggle__custom-icon-square {
          display: block;
          width: 5px;
          height: 5px;
          flex: 0 0 5px;
          background: currentColor;
        }
      `;

      document.head.appendChild(style);
    }

    renderButtonIcon() {
      if (!this.button) return;

      /*
       * 3 columns:
       * keep the theme's existing original icon unchanged.
       */
      if (this.cols === '3') {
        if (this.button.innerHTML !== this.originalButtonHTML) {
          this.button.innerHTML = this.originalButtonHTML;
        }

        return;
      }

      this.button.innerHTML = '';

      /*
       * 4 columns:
       * four centred squares in one row.
       */
      if (this.cols === '4') {
        const icon = document.createElement('span');

        icon.className =
          'collection-grid-toggle__custom-icon ' +
          'collection-grid-toggle__custom-icon--4';

        icon.setAttribute('aria-hidden', 'true');

        for (let i = 0; i < 4; i++) {
          const square = document.createElement('span');

          square.className =
            'collection-grid-toggle__custom-icon-square';

          icon.appendChild(square);
        }

        this.button.appendChild(icon);

        return;
      }

      /*
       * 5 columns:
       *
       *   ■ ■ ■
       *    ■ ■
       *
       * This keeps the icon centred within the same button
       * without the five squares pushing outside the edges.
       */
      if (this.cols === '5') {
        const icon = document.createElement('span');

        icon.className =
          'collection-grid-toggle__custom-icon ' +
          'collection-grid-toggle__custom-icon--5';

        icon.setAttribute('aria-hidden', 'true');

        const topRow = document.createElement('span');
        topRow.className =
          'collection-grid-toggle__custom-icon-row';

        const bottomRow = document.createElement('span');
        bottomRow.className =
          'collection-grid-toggle__custom-icon-row';

        for (let i = 0; i < 3; i++) {
          const square = document.createElement('span');

          square.className =
            'collection-grid-toggle__custom-icon-square';

          topRow.appendChild(square);
        }

        for (let i = 0; i < 2; i++) {
          const square = document.createElement('span');

          square.className =
            'collection-grid-toggle__custom-icon-square';

          bottomRow.appendChild(square);
        }

        icon.appendChild(topRow);
        icon.appendChild(bottomRow);

        this.button.appendChild(icon);
      }
    }

    apply() {
      this.setAttribute(
        'data-cols',
        this.cols
      );

      if (this.button) {
        this.button.setAttribute(
          'aria-label',
          `Show ${this.cols} products per row`
        );

        this.button.setAttribute(
          'aria-pressed',
          this.cols === '3'
            ? 'true'
            : 'false'
        );

        this.renderButtonIcon();
      }

      if (this.collection) {
        if (this.cols === this.defaultCols) {
          this.collection.style.removeProperty(
            '--products_per_row'
          );
        } else {
          this.collection.style.setProperty(
            '--products_per_row',
            this.cols
          );
        }
      }
    }
  }

  customElements.define(
    'collection-grid-toggle',
    CollectionGridToggle
  );
}


/*
 * ============================================================
 * COLLECTION PRODUCT VIEW TOGGLE
 * ============================================================
 *
 * STATE 1
 * Sleeve image
 * Sold-out products faded
 * Grey square icon
 *
 * STATE 2
 * Sleeve image
 * Sold-out products full colour
 * Black square icon
 *
 * STATE 3
 * Second image
 * First image on hover
 * Sold-out products faded
 * Grey circle icon
 *
 * STATE 4
 * Second image
 * First image on hover
 * Sold-out products full colour
 * Black circle icon
 *
 * The customer's choice is stored in localStorage.
 */

if (!customElements.get('collection-view-toggle')) {
  class CollectionViewToggle extends HTMLElement {
    connectedCallback() {
      this.button =
        this.querySelector('button');

      this.collection =
        this.closest('.collection');

      this.storageKey =
        this.dataset.storageKey ||
        'collection-grid-view-mode';

      this.allowedStates = [
        'sleeve-muted',
        'sleeve-full',
        'alternate-muted',
        'alternate-full'
      ];

      /*
       * State 1 is always the default.
       */
      this.state = 'sleeve-muted';

      /*
       * Restore the customer's previous choice.
       */
      if (!(window.Shopify && window.Shopify.designMode)) {
        try {
          const savedState =
            localStorage.getItem(
              this.storageKey
            );

          if (
            this.allowedStates.includes(
              savedState
            )
          ) {
            this.state = savedState;
          }

          /*
           * Backwards compatibility with the earlier
           * two-state version.
           */
          if (savedState === 'standard') {
            this.state = 'sleeve-muted';
          }

          if (savedState === 'alternate') {
            this.state = 'alternate-full';
          }
        } catch (e) {
          /* storage unavailable */
        }
      }

      this.addViewToggleStyles();
      this.apply();

      if (this.button) {
        this.button.addEventListener(
          'click',
          () => {
            const currentIndex =
              this.allowedStates.indexOf(
                this.state
              );

            const nextIndex =
              (
                currentIndex + 1
              ) % this.allowedStates.length;

            this.state =
              this.allowedStates[nextIndex];

            try {
              localStorage.setItem(
                this.storageKey,
                this.state
              );
            } catch (e) {
              /* storage unavailable */
            }

            this.apply();
          }
        );
      }
    }

    addViewToggleStyles() {
      if (
        document.getElementById(
          'collection-view-toggle-styles'
        )
      ) {
        return;
      }

      const style =
        document.createElement('style');

      style.id =
        'collection-view-toggle-styles';

      style.textContent = `
        collection-view-toggle .collection-view-toggle__button {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        collection-view-toggle .collection-view-toggle__icon {
          display: block;

          width: 12px;
          height: 12px;
          flex: 0 0 12px;

          transition:
            background-color 0.15s ease,
            border-radius 0.15s ease;
        }

        collection-view-toggle .collection-view-toggle__icon--square {
          border-radius: 0;
        }

        collection-view-toggle .collection-view-toggle__icon--circle {
          border-radius: 50%;
        }

        collection-view-toggle .collection-view-toggle__icon--muted {
          background: #B5B5B5;
        }

        collection-view-toggle .collection-view-toggle__icon--full {
          background: #000000;
        }
      `;

      document.head.appendChild(style);
    }

    getStateConfig() {
      switch (this.state) {
        case 'sleeve-full':
          return {
            alternateImages: false,
            soldOutFull: true,
            shape: 'square',
            tone: 'full',
            label:
              'Sleeve view with sold-out products full colour'
          };

        case 'alternate-muted':
          return {
            alternateImages: true,
            soldOutFull: false,
            shape: 'circle',
            tone: 'muted',
            label:
              'Alternate image view with sold-out products faded'
          };

        case 'alternate-full':
          return {
            alternateImages: true,
            soldOutFull: true,
            shape: 'circle',
            tone: 'full',
            label:
              'Alternate image view with sold-out products full colour'
          };

        case 'sleeve-muted':
        default:
          return {
            alternateImages: false,
            soldOutFull: false,
            shape: 'square',
            tone: 'muted',
            label:
              'Sleeve view with sold-out products faded'
          };
      }
    }

    renderButtonIcon(config) {
      if (!this.button) {
        return;
      }

      this.button.innerHTML = '';

      const icon =
        document.createElement('span');

      icon.className =
        `collection-view-toggle__icon ` +
        `collection-view-toggle__icon--${config.shape} ` +
        `collection-view-toggle__icon--${config.tone}`;

      icon.setAttribute(
        'aria-hidden',
        'true'
      );

      this.button.appendChild(icon);
    }

    apply() {
      const config =
        this.getStateConfig();

      this.setAttribute(
        'data-view-mode',
        this.state
      );

      if (this.collection) {
        this.collection.classList.toggle(
          'collection-view--alternate-images',
          config.alternateImages
        );

        this.collection.classList.toggle(
          'collection-view--soldout-full',
          config.soldOutFull
        );

        this.collection.classList.remove(
          'collection-view--alternate'
        );

        this.collection.classList.remove(
          'collection-view--standard'
        );

        this.collection.setAttribute(
          'data-collection-view',
          this.state
        );
      }

      if (this.button) {
        this.button.setAttribute(
          'aria-label',
          config.label
        );

        this.button.setAttribute(
          'title',
          config.label
        );

        this.button.setAttribute(
          'aria-pressed',
          this.state === 'sleeve-muted'
            ? 'false'
            : 'true'
        );

        this.renderButtonIcon(
          config
        );
      }
    }
  }

  customElements.define(
    'collection-view-toggle',
    CollectionViewToggle
  );
}